import React, { useMemo, useState } from 'react'

import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { Skeleton } from '@repo/ui'

import { UserBlock } from 'components/display/Table/components/Cells/Cells'
import { Search } from '../Search'

/**
 * VRG-565 — search + multi-select checklist of company users.
 *
 * ## Why this is a component and not a fifth copy
 *
 * working-conventions §28 names `AssignUsersModal` as the canonical implementation of
 * this pattern, and §36 says the SECOND hand-roll of a UI unit is a shared component.
 * By the time this was needed the pattern had been hand-rolled four times —
 * `AssignUsersModal`, `AssignCompaniesModal`, `CreateCertificateGroupModal` and
 * `LinkPackageModal` — and they had already drifted: three show a text "Loading…" line
 * where `LinkPackageModal` shows skeletons, which is the one §19 actually asks for.
 * Writing a fifth inline copy would be drift, not reuse.
 *
 * The behaviour is `AssignUsersModal`'s, verbatim, with two deliberate differences:
 * loading is a SKELETON rather than a "Loading users..." line (§19 — the skeleton
 * occupies the rows it replaces, so the list does not jump when it resolves), and the
 * empty-state copy is a prop because "all users already have this role" is not a
 * sentence every caller can use.
 *
 * The four existing copies are NOT migrated here — that is a sweep across four modals
 * with four different submit paths, and folding it into an unrelated ticket is exactly
 * the scope creep §50 warns about. It is reported as follow-up work.
 *
 * Presentational only: it owns the search box and the rows; the caller owns the
 * selection, so it can live in a form, a modal footer, or anywhere else.
 */

export interface UserChecklistUser {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  [key: string]: unknown
}

interface UserChecklistProps {
  users: UserChecklistUser[]
  /** Ids currently ticked. The caller owns this. */
  selectedIds: Set<string>
  onToggle: (userId: string) => void
  isLoading?: boolean
  searchPlaceholder?: string
  /** Shown when `users` is empty. */
  emptyMessage?: string
  /** Shown when the search filter matches nothing. */
  noMatchMessage?: string
  maxHeight?: number
}

export const UserChecklist = ({
  users,
  selectedIds,
  onToggle,
  isLoading = false,
  searchPlaceholder = 'Search users',
  emptyMessage = 'No users to show.',
  noMatchMessage = 'No users match your search.',
  maxHeight = 240,
}: UserChecklistProps) => {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return users
    const q = search.toLowerCase()
    return users.filter(user => {
      const name = [user.first_name, user.last_name].filter(Boolean).join(' ').toLowerCase()
      const email = (user.email ?? '').toLowerCase()
      return name.includes(q) || email.includes(q)
    })
  }, [users, search])

  return (
    <Stack spacing={3}>
      <Search
        value={search}
        onChange={setSearch}
        onClear={() => setSearch('')}
        placeholder={searchPlaceholder}
        fullWidth
        debounce={false}
      />

      <Box
        sx={theme => ({
          maxHeight,
          overflowY: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        })}
      >
        {isLoading ? (
          // §19 — footprint parity: three row-height bars in the same box, at the same
          // row height (40px avatar + 8px padding either side), so the list does not
          // resize when the real rows arrive.
          <Stack spacing={2} sx={{ p: 3 }}>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </Stack>
        ) : filtered.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <Typography variant="body3" color="text.secondary">
              {users.length === 0 ? emptyMessage : noMatchMessage}
            </Typography>
          </Stack>
        ) : (
          filtered.map((user, index) => (
            <Stack
              key={user.id}
              direction="row"
              alignItems="center"
              spacing={2}
              onClick={() => onToggle(user.id)}
              sx={theme => ({
                px: 3,
                py: 2,
                cursor: 'pointer',
                borderBottom:
                  index < filtered.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                '&:hover': { backgroundColor: theme.palette.action.hover },
              })}
            >
              <Checkbox
                checked={selectedIds.has(user.id)}
                onChange={() => onToggle(user.id)}
                // The row is the click target; without this the row handler and the
                // checkbox handler both fire and the tick immediately untoggles.
                onClick={e => e.stopPropagation()}
                size="small"
              />
              <UserBlock user={user} />
            </Stack>
          ))
        )}
      </Box>
    </Stack>
  )
}
